import { Component, Input, OnChanges, OnInit, ChangeDetectorRef } from '@angular/core';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { AppService } from 'common-cloudstudio';

interface TableDynamicCell {
  Type: 'text' | 'icon' | 'actions' | 'dropdown';
  Value?: string;
  IconClass?: string;
  Color?: string;
  Title?: string;
  Options?: string[];
  OnChange?: (row: TableDynamicRow, selectedStatus: string) => void;
  Buttons?: {
    Icon: string;
    Tooltip: string;
    Action: () => void;
    Class: string;
  }[];
}

interface TableDynamicRow {
  BackgroundColor?: any;
  Data: { [key: string]: TableDynamicCell };
}

interface TableColumn {
  Key: string;
  Title: string;
  WidthPixels?: number;
  Alignment?: number;
}

interface TableConfig {
  ShowHeader?: boolean;
  Columns?: TableColumn[];
  Rows?: TableDynamicRow[];
}

@Component({
  selector: 'cnm-alarm-history-table',
  templateUrl: './alarm-history-table.component.html',
  styleUrls: ['./alarm-history-table.component.scss'],
})
export class AlarmHistoryTableComponent implements OnInit, OnChanges {
  @Input() datos: { Table?: TableConfig };
  @Input() height: number;
  @Input() statusZoom: boolean = false;

  minHeight: number = 0;
  elementHeight: string = '270px';
  status = 0;
  tableDataDynamic: TableDynamicRow[] = [];
  columns: TableColumn[] = [];

  constructor(public _appService: AppService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    // Usar setTimeout para asegurar que los datos del input estén disponibles
    setTimeout(() => {
      this.processData();
    }, 0);
  }

  ngOnChanges(): void {
    // Procesar datos cuando cambian los inputs
    setTimeout(() => {
      this.processData();
    }, 0);
  }

  private processData(): void {
    // Verificar si hay datos del input
    if (!this.datos || !this.datos.Table) {
      this.tableDataDynamic = [];
      this.columns = [];
      this.status = 2; // Sin datos
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      return;
    }

    // Obtener columnas
    this.columns = this.datos.Table.Columns || [];
    
    // Obtener filas
    const rows = this.datos.Table.Rows || [];
    
    if (rows && rows.length > 0) {
      // Crear una copia de las filas para asegurar la detección de cambios
      this.tableDataDynamic = [...rows];
      this.configureDropdowns();
      this.status = 1; // Datos disponibles
    } else {
      this.tableDataDynamic = [];
      this.status = 2; // Sin datos
    }

    // Ajustar altura si aplica
    if (this.height) {
      this.elementHeight = (this.height - 30) + "px";
    }

    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  private configureDropdowns(): void {
    this.tableDataDynamic.forEach(row => {
      Object.keys(row.Data).forEach(key => {
        const cell = row.Data[key];
        if (cell.Type === 'dropdown' && typeof cell.OnChange !== 'function') {
          cell.OnChange = (row: TableDynamicRow, selectedStatus: string) => {
            this.changeStatus(row, selectedStatus, key);
          };
        }
      });
    });
  }

  rgbToHexa(rgb: any) {
    var value = '000000' + rgb.toString(16);
    return '#' + value.substring(value.length - 6);
  }

  getAlignment(alignmentCode: number) {
    switch (alignmentCode) {
      case 0: return 'left';
      case 1: return 'right';
      case 2: return 'center';
      default: return 'left';
    }
  }

  changeStatus(row: TableDynamicRow, selectedStatus: string, columnKey: string) {
    if (row.Data?.[columnKey]) {
      row.Data[columnKey].Value = selectedStatus;
      this.cdr.detectChanges();
    }
  }

  onDropdownChange(row: TableDynamicRow, columnKey: string, selectedValue: string, dropdown: BsDropdownDirective) {
    this.changeStatus(row, selectedValue, columnKey);
    
    if (dropdown) {
      dropdown.hide();
    }
    
    if (row.Data[columnKey]?.OnChange) {
      row.Data[columnKey].OnChange(row, selectedValue);
    }
    
    this.cdr.detectChanges();
  }

  export(formatType: string) {
    if (!this.tableDataDynamic?.length) { return; }

    let fileNumber = Math.floor(1000 + Math.random() * 9000);
    let csvContent = "";

    if (!this.columns || this.columns.length === 0) { return; }

    // Encabezados
    this.columns.forEach((col, i) => {
      csvContent += this._appService.makeCsvColumn(
        col.Title,
        i === this.columns.length - 1
      );
    });

    // Filas
    for (let row of this.tableDataDynamic) {
      let csvRow = '';
      for (let col of this.columns) {
        const cell = row.Data[col.Key];
        let value = cell?.Value || '';
        if (cell?.Type === 'icon' && cell.Title) {
          value = cell.Title;
        }
        csvRow += this._appService.makeCsvColumn(
          value,
          col === this.columns[this.columns.length - 1]
        );
      }
      csvContent += csvRow;
    }

    const encodedUri = "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `table${fileNumber}_${new Date().toLocaleString()}.csv`);
    document.body.appendChild(link);
    link.click();
  }
}
